import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  activityCard: {
    border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px",
    marginBottom: "12px", borderLeft: "4px solid #1a237e"
  },
  activityTitle: { fontWeight: "bold", color: "#1a237e", fontSize: "15px", margin: "0 0 6px" },
  activityBody: { color: "#444", fontSize: "13px", lineHeight: "1.6", margin: "0 0 8px" },
  activityMeta: { fontSize: "11px", color: "#999" },
  filterRow: { display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" },
  emptyState: {
    textAlign: "center", padding: "40px", color: "#999"
  },
  emptyIcon: { fontSize: "48px", color: "#e0e0e0", display: "block", marginBottom: "12px" }
};

const TYPE_COLORS = {
  alumni:   { bg: "#e8f5e9", text: "#2e7d32", border: "#2e7d32" },
  events:   { bg: "#f3e5f5", text: "#6a1b9a", border: "#6a1b9a" },
  general:  { bg: "#e3f2fd", text: "#0d47a1", border: "#1a237e" },
  academic: { bg: "#fff8e1", text: "#e65100", border: "#e65100" }
};

const BLANK = { title: "", message: "", category: "alumni", audience: "all" };

const STATIC_ACTIVITIES = [
  {
    id: "static-1",
    title: "Annual Alumni Homecoming 2026",
    message: "Join us for the annual alumni homecoming event. Reconnect with classmates and celebrate the achievements of the graduating class.",
    category: "alumni",
    audience: "all",
    createdAt: "2026-01-15",
    createdBy: "Admin"
  },
  {
    id: "static-2",
    title: "Alumni Mentorship Programme",
    message: "We are looking for alumni to volunteer as mentors for current students. Help shape the next generation of Bishop Willis graduates.",
    category: "alumni",
    audience: "all",
    createdAt: "2026-02-01",
    createdBy: "Admin"
  },
  {
    id: "static-3",
    title: "Fundraising Drive — New Library Wing",
    message: "Help us raise funds for the new library wing. Alumni contributions of any size are welcome and appreciated.",
    category: "alumni",
    audience: "all",
    createdAt: "2026-03-10",
    createdBy: "Admin"
  }
];

class AlumniActivities extends Component {
  state = {
    activities: [],
    loading: true,
    usingStatic: false,
    open: false,
    form: { ...BLANK },
    saving: false,
    error: null,
    typeFilter: ""
  };

  componentDidMount() { this.fetchActivities(); }

  fetchActivities = async () => {
    this.setState({ loading: true });
    try {
      const { typeFilter } = this.state;
      const params = { limit: 50, sort: "createdAt", order: "desc", category: "alumni" };
      if (typeFilter) params.category = typeFilter;
      const res = await api.get("/notices", { params });
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      if (list.length === 0 && !typeFilter) {
        this.setState({ activities: STATIC_ACTIVITIES, loading: false, usingStatic: true });
      } else {
        this.setState({ activities: list, loading: false, usingStatic: false });
      }
    } catch (e) {
      this.setState({ activities: STATIC_ACTIVITIES, loading: false, usingStatic: true });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  handleSave = async () => {
    const { form } = this.state;
    if (!form.title || !form.message) {
      this.setState({ error: "Title and message are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/notices", { ...form, category: "alumni" });
      this.setState({ open: false, saving: false, usingStatic: false });
      this.fetchActivities();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Could not post activity. Check connectivity.", saving: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { activities, loading, usingStatic, open, form, saving, error, typeFilter } = this.state;

    const filters = [
      { value: "", label: "All" },
      { value: "alumni", label: "Alumni" },
      { value: "events", label: "Events" },
      { value: "general", label: "General" }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Alumni Activities & Events</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    News, events, and announcements for the alumni community
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={() => this.setState({ open: true, form: { ...BLANK }, error: null })}>
                    <Icon style={{ marginRight: "4px" }}>add</Icon> Post Activity
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              {usingStatic && (
                <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: "6px", padding: "10px 14px", marginBottom: "14px", fontSize: "13px", color: "#e65100" }}>
                  <Icon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "6px" }}>info</Icon>
                  Showing sample activities. Connect to the backend to manage real alumni events.
                </div>
              )}

              <div className={classes.filterRow}>
                {filters.map(f => (
                  <Button
                    key={f.value || "all"}
                    size="sm"
                    onClick={() => this.setState({ typeFilter: f.value }, this.fetchActivities)}
                    style={{
                      background: typeFilter === f.value ? "#1a237e" : "#f5f5f5",
                      color: typeFilter === f.value ? "#fff" : "#333",
                      boxShadow: "none"
                    }}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>

              {loading ? (
                <div style={{ textAlign: "center", padding: "30px" }}><CircularProgress /></div>
              ) : activities.length === 0 ? (
                <div className={classes.emptyState}>
                  <Icon className={classes.emptyIcon}>event_note</Icon>
                  <p>No alumni activities posted yet.</p>
                  <Button size="sm" color="primary"
                    onClick={() => this.setState({ open: true, form: { ...BLANK }, error: null })}>
                    Post First Activity
                  </Button>
                </div>
              ) : (
                <GridContainer>
                  {activities.map(a => {
                    const cc = TYPE_COLORS[a.category] || TYPE_COLORS.general;
                    return (
                      <GridItem xs={12} sm={6} md={4} key={a.id}>
                        <div className={classes.activityCard} style={{ borderLeftColor: cc.border }}>
                          <Chip
                            label={a.category || "alumni"}
                            style={{ background: cc.bg, color: cc.text, fontSize: "11px", height: "20px", marginBottom: "8px" }}
                          />
                          <p className={classes.activityTitle}>{a.title}</p>
                          <p className={classes.activityBody}>
                            {a.message && a.message.length > 120 ? a.message.substring(0, 120) + "..." : a.message}
                          </p>
                          <p className={classes.activityMeta}>
                            {a.audience && (
                              <span style={{ marginRight: "8px" }}>
                                <Icon style={{ fontSize: "12px", verticalAlign: "middle" }}>group</Icon> {a.audience}
                              </span>
                            )}
                            {a.createdAt ? new Date(a.createdAt).toLocaleDateString("en-UG") : ""}
                            {(a.author?.name || a.createdBy) ? ` · ${a.author?.name || a.createdBy}` : ""}
                          </p>
                        </div>
                      </GridItem>
                    );
                  })}
                </GridContainer>
              )}
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Post Alumni Activity" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Title *" id="actTitle" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.title, onChange: this.handleChange("title") }} />
            <div style={{ marginTop: "16px" }}>
              <label style={{ fontSize: "12px", color: "#aaa", textTransform: "uppercase" }}>Details *</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={this.handleChange("message")}
                style={{ width: "100%", marginTop: "4px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
                placeholder="Describe the activity or event..."
              />
            </div>
            <GridContainer>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Category</InputLabel>
                  <Select value={form.category} onChange={this.handleChange("category")}>
                    <MenuItem value="alumni">Alumni</MenuItem>
                    <MenuItem value="events">Events</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="academic">Academic</MenuItem>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Audience</InputLabel>
                  <Select value={form.audience} onChange={this.handleChange("audience")}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="students">Students</MenuItem>
                    <MenuItem value="staff">Staff</MenuItem>
                    <MenuItem value="alumni">Alumni</MenuItem>
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Post"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

AlumniActivities.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(AlumniActivities));
